/**
 * Subscription plan feature flags (company.subscriptionPlanDetails.features).
 * Legacy companies without a plan treat all features as enabled.
 */

export const SUBSCRIPTION_FEATURE_KEYS = {
  autoProcessing: 'autoProcessing',
  emailNotifications: 'emailNotifications',
  masterDataHandling: 'masterDataHandling',
  apiIntegrations: 'apiIntegrations',
  peppolIntegration: 'peppolIntegration',
};

export const isSubscriptionPlanFeatureEnabled = (
  subscriptionPlanDetails,
  featureKey
) => {
  const features = subscriptionPlanDetails?.features;
  if (!features || features[featureKey] === undefined) {
    return true;
  }
  return Boolean(features[featureKey]);
};

export const getSubscriptionPlanFeatureAccess = (subscriptionPlanDetails) => ({
  autoProcessing: isSubscriptionPlanFeatureEnabled(
    subscriptionPlanDetails,
    SUBSCRIPTION_FEATURE_KEYS.autoProcessing
  ),
  emailNotifications: isSubscriptionPlanFeatureEnabled(
    subscriptionPlanDetails,
    SUBSCRIPTION_FEATURE_KEYS.emailNotifications
  ),
  masterDataHandling: isSubscriptionPlanFeatureEnabled(
    subscriptionPlanDetails,
    SUBSCRIPTION_FEATURE_KEYS.masterDataHandling
  ),
});

/** Clear toggles that are not allowed by the subscription plan. */
export const applySubscriptionFeatureConstraints = (
  subscriptionPlanDetails,
  values
) => {
  const access = getSubscriptionPlanFeatureAccess(subscriptionPlanDetails);
  const next = { ...values };

  if (!access.autoProcessing) {
    next.autoProcess = false;
  }

  if (!access.emailNotifications) {
    next.emailNotification = false;
    next.autoEmailSuccessfulValidation = false;
    next.autoEmailSuccessfulValidationToTradingPartner = false;
    next.autoEmailFailedValidation = false;
    next.autoEmailPreSubmissionFailure = false;
    next.autoEmailDuplicateInvoices = false;
    next.autoEmailTradingPartnerErrorLog = false;
    next.autoEmailIncompleteTradingPartner = false;
    next.autoEmailScheduledMaintenance = false;
  }

  return next;
};

export const SUBSCRIPTION_FEATURE_DISABLED_HINT =
  'This option is not included in the company subscription plan.';

export const SUBSCRIPTION_MASTER_DATA_DISABLED_HINT =
  'Master Data is not included in the company subscription plan.';

/** Subscription plan user-creation actions (company.subscriptionPlanDetails.actions). */
export const SUBSCRIPTION_ACTION_KEYS = {
  canAddGroupAdmin: 'canAddGroupAdmin',
  canAddCompanyAdmin: 'canAddCompanyAdmin',
  canAddGeneralUser: 'canAddGeneralUser',
};

export const ROLE_TO_SUBSCRIPTION_ACTION = {
  'Group Admin': SUBSCRIPTION_ACTION_KEYS.canAddGroupAdmin,
  'Company Admin': SUBSCRIPTION_ACTION_KEYS.canAddCompanyAdmin,
  'General User': SUBSCRIPTION_ACTION_KEYS.canAddGeneralUser,
};

export const SUBSCRIPTION_ROLE_ACTION_DISABLED_HINTS = {
  [SUBSCRIPTION_ACTION_KEYS.canAddGroupAdmin]:
    'Adding Group Admin users is not included in the company subscription plan.',
  [SUBSCRIPTION_ACTION_KEYS.canAddCompanyAdmin]:
    'Adding Company Admin users is not included in the company subscription plan.',
  [SUBSCRIPTION_ACTION_KEYS.canAddGeneralUser]:
    'Adding General User accounts is not included in the company subscription plan.',
};

export const isSubscriptionPlanActionEnabled = (
  subscriptionPlanDetails,
  actionKey
) => {
  const actions = subscriptionPlanDetails?.actions;
  if (!actions || actions[actionKey] === undefined) {
    return true;
  }
  return Boolean(actions[actionKey]);
};

export const getSubscriptionPlanActionAccess = (subscriptionPlanDetails) => ({
  canAddGroupAdmin: isSubscriptionPlanActionEnabled(
    subscriptionPlanDetails,
    SUBSCRIPTION_ACTION_KEYS.canAddGroupAdmin
  ),
  canAddCompanyAdmin: isSubscriptionPlanActionEnabled(
    subscriptionPlanDetails,
    SUBSCRIPTION_ACTION_KEYS.canAddCompanyAdmin
  ),
  canAddGeneralUser: isSubscriptionPlanActionEnabled(
    subscriptionPlanDetails,
    SUBSCRIPTION_ACTION_KEYS.canAddGeneralUser
  ),
});

export const canCreateUserRole = (
  subscriptionPlanDetails,
  roleName,
  isExempt = false
) => {
  if (isExempt) {
    return true;
  }
  const actionKey = ROLE_TO_SUBSCRIPTION_ACTION[roleName];
  if (!actionKey) {
    return true;
  }
  return isSubscriptionPlanActionEnabled(subscriptionPlanDetails, actionKey);
};

export const getUserRoleCreationDisabledHint = (roleName) => {
  const actionKey = ROLE_TO_SUBSCRIPTION_ACTION[roleName];
  if (!actionKey) {
    return SUBSCRIPTION_FEATURE_DISABLED_HINT;
  }
  return (
    SUBSCRIPTION_ROLE_ACTION_DISABLED_HINTS[actionKey] ||
    SUBSCRIPTION_FEATURE_DISABLED_HINT
  );
};
